import React, { FC, Fragment, ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Grid, Tabs, makeStyles } from '@material-ui/core';
import EmailIcon from '@material-ui/icons/Email';

import { UserSegment, UserStatus, SearchSortFieldsV2 } from 'api';
import { LargeTab, Queries } from 'components';
import { AdminContentEmailCreate } from 'Links';
import { CONTENT_EMAIL_LIMIT } from 'constants/index';

import ScheduledEmails from '../components/Emails/Scheduled';
import SentEmails from '../components/Emails/Sent';
import Loader from '../components/Emails/Loader';

interface IQueryResults {
  segments: UserSegment[];
  numActiveUsers: number;
}

interface IScheduledResults extends IQueryResults {
  scheduledEmails: Queries.IGetContentEmailSpecsResult;
}

interface ISentResults extends IQueryResults {
  sentEmails: Queries.IGetContentEmailHistoryResult;
}

type TabValues = 'recurring' | 'non-recurring' | 'sent';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(4),
  },
}));

const USER_SEARCH_VARIABLES = {
  user_status: UserStatus.Active,
  q: '',
  size: 1,
  sort: SearchSortFieldsV2.FullName,
};

const Emails: FC = () => {
  const [tab, setTab] = useState<TabValues>('recurring');
  const { t } = useTranslation();
  const classes = useStyles();

  function handleTabChange(event: ChangeEvent<HTMLDivElement>, value: TabValues) {
    setTab(value);
  }

  function renderScheduledEmailList(queryResults: IScheduledResults) {
    const { segments, scheduledEmails, numActiveUsers } = queryResults;
    return (
      <ScheduledEmails
        segments={segments}
        numActiveUsers={numActiveUsers}
        scheduledEmails={scheduledEmails}
      />
    );
  }

  function renderSentEmailList(queryResults: ISentResults) {
    const { segments, sentEmails } = queryResults;
    return <SentEmails segments={segments} sentEmails={sentEmails} />;
  }

  return (
    <Fragment>
      <Grid container alignItems="center" justify="space-between">
        <Grid item>
          <Tabs value={tab} onChange={handleTabChange}>
            <LargeTab label={t('common:recurring')} value="recurring" />
            <LargeTab label={t('common:nonRecurring')} value="non-recurring" />
            <LargeTab label={t('common:sent')} value="sent" />
          </Tabs>
        </Grid>
        <Grid item>
          <Button
            color="primary"
            variant="contained"
            component={AdminContentEmailCreate}
            startIcon={<EmailIcon />}>
            {t('contentEmails:newEmail')}
          </Button>
        </Grid>
      </Grid>
      <div className={classes.root}>
        <Queries.GetUserSegments loader={<Loader rows={5} />}>
          {segments => (
            <Queries.GetUserSearchesV2
              key="table"
              loader={<Loader rows={5} />}
              variables={USER_SEARCH_VARIABLES}>
              {(profiles, numActiveUsers, onLoadMore) => (
                <Fragment>
                  {tab === 'recurring' && (
                    <Queries.GetContentEmailSpecs
                      variables={{ data: { recurringOnly: true, limit: CONTENT_EMAIL_LIMIT } }}
                      loader={<Loader rows={5} />}>
                      {scheduledEmails =>
                        renderScheduledEmailList({ scheduledEmails, segments, numActiveUsers })
                      }
                    </Queries.GetContentEmailSpecs>
                  )}
                  {tab === 'non-recurring' && (
                    <Queries.GetContentEmailSpecs
                      variables={{ data: { recurringOnly: false, limit: CONTENT_EMAIL_LIMIT } }}
                      loader={<Loader rows={5} />}>
                      {scheduledEmails =>
                        renderScheduledEmailList({ scheduledEmails, segments, numActiveUsers })
                      }
                    </Queries.GetContentEmailSpecs>
                  )}
                  {tab === 'sent' && (
                    <Queries.GetContentEmailHistory loader={<Loader rows={5} />}>
                      {sentEmails => renderSentEmailList({ sentEmails, segments, numActiveUsers })}
                    </Queries.GetContentEmailHistory>
                  )}
                </Fragment>
              )}
            </Queries.GetUserSearchesV2>
          )}
        </Queries.GetUserSegments>
      </div>
    </Fragment>
  );
};

export default Emails;
